import { useEffect, useState } from 'react';
import { ModelList } from './components/ModelList';
import { ModelDetail } from './components/ModelDetail';
import { SearchFilters } from './components/SearchFilters';
import { HomeScreen } from './components/HomeScreen';
import { BottomNav } from './components/BottomNav';
import { Onboarding } from './components/Onboarding';
import { useFavorites } from './hooks/useFavorites';
import { Model, FilterState } from './types';
import './App.css';

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        ready: () => void;
        expand: () => void;
        showAlert: (message: string) => void;
        initDataUnsafe?: { user?: { id: number } };
      };
    };
  }
}

const ONBOARDED_KEY = 'mih_onboarded_v1';

type Tab = 'home' | 'explore';

function App() {
  const WebApp = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
  const { favorites, toggle: toggleFavorite } = useFavorites();

  const [tab, setTab] = useState<Tab>('home');
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(ONBOARDED_KEY));

  const [models, setModels] = useState<Model[]>([]);
  const [totalModels, setTotalModels] = useState(0);
  const [filters, setFilters] = useState<FilterState>({
    freeOnly: false,
    noDeposit: false,
    privacyFirst: false,
    searchQuery: '',
    modality: '',
    bestFor: '',
    accessChannel: '',
    sortBy: 'updated',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    WebApp?.ready();
    WebApp?.expand();
    fetchModels();
  }, []);

  useEffect(() => {
    if (tab === 'explore') fetchModels();
  }, [filters, tab]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filters.freeOnly) q.append('free', 'true');
      if (filters.noDeposit) q.append('noDeposit', 'true');
      if (filters.privacyFirst) q.append('privacyFirst', 'true');
      if (filters.searchQuery) q.append('search', filters.searchQuery);
      if (filters.modality) q.append('modality', filters.modality);
      if (filters.bestFor) q.append('bestFor', filters.bestFor);
      if (filters.accessChannel) q.append('accessChannel', filters.accessChannel);
      if (filters.sortBy) q.append('sortBy', filters.sortBy);
      const userId = WebApp?.initDataUnsafe?.user?.id;
      if (userId) q.append('userId', userId.toString());

      const res = await fetch(`/api/v1/models?${q}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setModels(data.data || []);
      setTotalModels(data.pagination?.total || 0);
    } catch {
      setError('Failed to load models');
    } finally {
      setLoading(false);
    }
  };

  const handleDoneOnboarding = () => {
    localStorage.setItem(ONBOARDED_KEY, '1');
    setShowOnboarding(false);
  };

  // Detail view takes over any tab
  if (selectedModel) {
    return (
      <>
        <ModelDetail model={selectedModel} onBack={() => setSelectedModel(null)} />
        {showOnboarding && <Onboarding onDone={handleDoneOnboarding} />}
      </>
    );
  }

  return (
    <div className="app">
      {tab === 'home' && (
        <HomeScreen
          favorites={favorites}
          onSelectModel={setSelectedModel}
          onGoExplore={() => setTab('explore')}
        />
      )}

      {tab === 'explore' && (
        <>
          <header className="header">
            <h1>🔍 Explore Models</h1>
            <p className="subtitle">{models.length} of {totalModels} models</p>
          </header>
          <SearchFilters filters={filters} onChange={setFilters} />
          {loading ? (
            <div className="loading">Loading...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <ModelList
              models={models}
              onSelect={setSelectedModel}
              favorites={favorites}
              onToggleFavorite={toggleFavorite}
            />
          )}
        </>
      )}

      <BottomNav tab={tab} onChange={setTab} favoritesCount={favorites.size} />

      {showOnboarding && <Onboarding onDone={handleDoneOnboarding} />}
    </div>
  );
}

export default App;
